import { Button } from '@cian/ui-kit';
import { TButtonSize } from '@cian/ui-kit/button/types';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PopupOwnOfferContainer } from 'shared/components/popups/own_offer/container';
import { useSimpleBotDetector } from 'shared/hooks/useSimpleBotDetector';
import {
  getAgentAvailability,
  getPhones,
  getVillageId,
  isNewbuildingFromBuilderSelector,
  isNewbuildingFromSellerSelector,
  selectIsOfferPublished,
  selectOffer,
  selectUser,
} from 'shared/selectors';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';
import { isRenterFormEnabled } from 'shared/selectors/isRenterFormEnabled';
import { selectIsChatsEnabled } from 'shared/selectors/offerChat';
import { getRosreestrInfo } from 'shared/selectors/offerData/getRosreestrInfo';
import { isPrintVersionPhoneVisibilitySelector } from 'shared/selectors/print';
import { selectAgent } from 'shared/selectors/selectAgent';
import { prefetchFormState } from 'shared/store/deal-rent';
import { setNotAvailablePopupOpenValue } from 'shared/store/notAvailablePopupOpen';
import { setPhoneCollapse } from 'shared/store/phone_collapse';
import {
  trackBlockedEarlyAccessPhoneClick,
  trackShowEarlyAccessPhoneClick,
} from 'shared/utils/tracking/earlyAccessTracking';

import { PhonesOpened } from './PhonesOpened';
import { trackShowPhoneClick } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../../packages/EarlyAccessPaywall';
import {
  EAccessCheckTrackingStatus,
  EStatus,
  ESubscriptionLimitType,
} from '../../../../packages/EarlyAccessPaywall/shared/types/context';
import { useApplicationContext } from '../../ApplicationContext';

interface IPhonesContainerProps {
  size?: TButtonSize;
  trackingAction?: string;
}

export const PhonesContainer: React.FC<React.PropsWithChildren<IPhonesContainerProps>> = ({ size = 'M' }) => {
  const [isPhonesOpened, setPhonesOpened] = React.useState(false);
  const dispatch = useDispatch();
  const { logger, telemetry } = useApplicationContext();
  const isPublished = useSelector(selectIsOfferPublished);
  const offer = useSelector(selectOffer);
  const phones = useSelector(getPhones);
  const isChatsEnabled = useSelector(selectIsChatsEnabled);
  const isDealRentFormEnabled = useSelector(isRenterFormEnabled);
  const agent = useSelector(selectAgent);
  const agentAvailability = useSelector(getAgentAvailability);
  const user = useSelector(selectUser);
  const rosreestrInfo = useSelector(getRosreestrInfo);
  const villageId = useSelector(getVillageId);
  const isNewbuildingFromBuilder = useSelector(isNewbuildingFromBuilderSelector);
  const isNewbuildingFromSeller = useSelector(isNewbuildingFromSellerSelector);

  const isPrintVersion = useSelector(isPrintVersionPhoneVisibilitySelector);
  const botDetector = useSimpleBotDetector();

  const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
  const [isOwnOfferPopupOpen, setOwnOfferPopupOpen] = React.useState(false);

  const { checkContactsAccess, status } = useEarlyAccessPaywallContext();
  const isEarlyAccessContactsFlowEnabled = useSelector(selectEarlyAccessContactsFlowEnabled);

  const startChat = () => {
    const isOfferOwnedByUser = Boolean(user && user.isAuthenticated && user.realtyUserId === offer.userId);
    const canShowAvailability = isChatsEnabled && isAgentNotAvailable;

    if (!canShowAvailability) {
      return;
    }

    switch (true) {
      case isOfferOwnedByUser:
        return setOwnOfferPopupOpen(true);
      default:
        dispatch(setNotAvailablePopupOpenValue(true));
    }
  };

  const ownOfferPopupClose = () => setOwnOfferPopupOpen(false);

  const onShowPhoneClick = () => {
    if (isDealRentFormEnabled) {
      dispatch(prefetchFormState());

      return;
    }

    if (isNewbuildingFromBuilder || isNewbuildingFromSeller) {
      telemetry.increment('newbuilding.show_phone_click.under_description');
    }

    setPhonesOpened(true);
    dispatch(setPhoneCollapse());
  };

  const trackShowPhone = () => {
    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;

    trackShowPhoneClick({
      offer,
      agent,
      user,
      kpId: villageId,
      bot: !botDetector.isHuman(),
      isAgentNotAvailable,
      rosreestrInfo,
    });
  };

  const handleEarlyAccessFlow = async () => {
    const commonTrackingParams = { source: 'card_more_block', offer, agent, user };

    if (!user?.isAuthenticated) {
      trackBlockedEarlyAccessPhoneClick({
        ...commonTrackingParams,
        blockedType: EAccessCheckTrackingStatus.Unauthorized,
      });
    }

    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: offer.id,
      limitType: ESubscriptionLimitType.PhoneNumber,
    });

    if (isConfirmed) {
      trackShowEarlyAccessPhoneClick(commonTrackingParams);
      onShowPhoneClick();

      return;
    }

    if (trackingStatus) {
      trackBlockedEarlyAccessPhoneClick({ ...commonTrackingParams, blockedType: trackingStatus });
    }
  };

  const handleButtonClick: VoidFunction = async () => {
    if (isEarlyAccessContactsFlowEnabled) {
      await handleEarlyAccessFlow();

      return;
    }

    trackShowPhone();
    onShowPhoneClick();
    startChat();
  };

  if (!isPublished) {
    return null;
  }

  if (!phones.length || phones.some(phone => !phone.countryCode || !phone.number)) {
    logger.error(`Offer phones is null`, { cianId: offer.cianId });

    return null;
  }

  return (
    <div>
      {(isPhonesOpened || isPrintVersion) && <PhonesOpened />}
      {!isPhonesOpened && !isPrintVersion && (
        <Button
          data-name="CallToAuthorPhoneButton"
          theme={'fill_primary'}
          size={size}
          onClick={handleButtonClick}
          loading={status === EStatus.Loading}
        >
          Показать контакты
        </Button>
      )}
      <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={ownOfferPopupClose} />
    </div>
  );
};
